import { createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import getConfig from "../config";
import { buildMultiTaxRequestParams } from "../features/search/Filters/mapMultiTaxToApiParams";
import {
    customerRelationshipOptions,
    getCustomerRelationShip,
    processIdentifiers,
} from "../features/search/Filters/utility";
import toast from "../hooks/toast";
import { buildApiV3Url, buildApiV3UrlWithQuery, fixSearchStringForPostgres } from "../shared/Utils";

// Config is retrieved lazily inside functions to support runtime configuration

const defaultFormState = {
    nameOrCode: "",
    nameOrCodeWhileLoading: "",
};

const defaultRequestFiltersState = {
    title: "",
    customMessage: "",
    attachCertificates: false,
    isCustomerDetails: true,
    isCertificateDetails: false,
    customerRegions: [],
    isMissingcertificates: false,
    missingExposureZones: [],
    isBillToRelation: false,
    isShipToRelation: false,
    customerRelationShip: [],
    isVendor: false,
    customerCodes: [],
    customerCodeError: "",
    customerCreatedDateAfter: "",
    customerCreatedDateBefore: "",
    customerCreatedDateAfterError: "",
    customerCreatedDateBeforeError: "",
    customerModifiedDateAfter: "",
    customerModifiedDateBefore: "",
    customerModifiedDateAfterError: "",
    customerModifiedDateBeforeError: "",
    certificatesLabels: [],
    excludeCertificateLabels: [],
    customersLabels: [],
    excludeCustomerLabels: [],
    certificateStatus: [],
    certificateRegions: [],
    exemptReasons: [],
    invalidReasons: [],
    isPurchaseOrder: false,
    invoiceNumber: "",
    certificateIds: [],
    certificateIdError: "",
    isCertificateImage: 0,
    certificateCreatedDateAfter: "",
    certificateCreatedDateBefore: "",
    certificateModifiedDateAfter: "",
    certificateModifiedDateBefore: "",
    certificateEffectiveDateAfter: "",
    certificateEffectiveDateBefore: "",
    certificateExpirationDateAfter: "",
    certificateExpirationDateBefore: "",
    certificateValidatedDateAfter: "",
    certificateValidatedDateBefore: "",
    isCertActive: 0,
    savedSearch: [],
    alternateId: "",
    isExportCampaignDialogOpen: false,
    isExportCampaignLoading: false,
    isDuplicateRelation: false,
    city: "",
    zip: "",
    address: "",
    phone: "",
    federalTaxID: "",
    email: "",
    customerExposures: [],
    localJurisdictions: [],
    isExemptionOverrideDetails: false,
    overrideExemptReasons: [],
    overrideRegions: [],
    overrideCreatedDateAfter: "",
    overrideCreatedDateBefore: "",
    overrideEffectiveDateAfter: "",
    overrideEffectiveDateBefore: "",
    overrideExpirationDateAfter: "",
    overrideExpirationDateBefore: "",
    overrideLicenseNumber: "",
    isOverrideActive: 0,
};

const defaultPageData = {
    data: [],
    paginate: {
        pageNumber: 0,
        startIndex: -1,
        endIndex: 0,
        rowsPerPage: 20,
        totalRecords: 0,
    },
};

const [showToast, hideToast] = toast();

export const requestSlice = createSlice({
    name: "request",
    initialState: {
        formState: defaultFormState,
        requestFiltersState: defaultRequestFiltersState,
        isloading: false,
        isCustomerSearchLoading: false,
        isCustomersWithoutEmailLoading: false,
        isCampaignCustomersLoading: false,
        campaign: [],
        noDataType: "",
        page: defaultPageData,
        customerSearchPage: defaultPageData,
        campaignCustomersPage: defaultPageData,
        sortColumn: ["created", false],
        createSortColumn: ["name", true],
        detailsSortColumn: ["id", true],
        missingCustomerEmailCount: 0,
        isCustomerCountAPICompleted: 0,
        isLoadingCustomerCount: false,
        selectedCustomerCount: 0,
        isSelectedCustomersCountLoading: false,
        addedCustomers: [],
        removedCustomers: [],
    },
    reducers: {
        setLoading: (state, action) => {
            state.isloading = action.payload;
        },
        setCustomerSearchLoading: (state, action) => {
            state.isCustomerSearchLoading = action.payload;
        },
        setCustomersWithoutEmailLoading: (state, action) => {
            state.isCustomersWithoutEmailLoading = action.payload;
        },
        setCampaignCustomersLoading: (state, action) => {
            state.isCampaignCustomersLoading = action.payload;
        },
        setFormState: (state, action) => {
            state.formState = { ...state.formState, ...action.payload };
        },
        clearFormState: state => {
            state.formState = defaultFormState;
        },
        setRequestFiltersState: (state, action) => {
            const key = Object.keys(action.payload)[0];
            const value = Object.values(action.payload)[0];
            state.requestFiltersState[key] = value;

            if (
                (key === "certificateRegions" || key === "exemptReasons") &&
                state.requestFiltersState.localJurisdictions?.length > 0
            ) {
                state.requestFiltersState.localJurisdictions = [];
            }
        },
        clearRequestFiltersState: state => {
            state.requestFiltersState = defaultRequestFiltersState;
        },
        setCampaign: (state, action) => {
            state.campaign = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setCustomerSearchPage: (state, action) => {
            state.customerSearchPage = action.payload;
        },
        setCampaignCustomersPage: (state, action) => {
            state.campaignCustomersPage = action.payload;
        },
        setPageToNull: state => {
            state.page = defaultPageData;
        },
        setCustomerSearchPageToNull: state => {
            state.customerSearchPage = defaultPageData;
        },
        setCampaignCustomersPageToNull: state => {
            state.campaignCustomersPage = defaultPageData;
        },
        setNoDataType: (state, action) => {
            state.noDataType = action.payload;
        },
        setNoDataOnSearch: state => {
            state.noDataType = "onSearch";
        },
        setNoDataOnLand: state => {
            state.noDataType = "onLanding";
        },
        setNoDataToNull: state => {
            state.noDataType = "";
        },
        setSortColumn: (state, action) => {
            state.sortColumn = action.payload;
        },
        setCreateSortColumn: (state, action) => {
            state.createSortColumn = action.payload;
        },
        setDetailsSortColumn: (state, action) => {
            state.detailsSortColumn = action.payload;
        },
        setMissingCustomerEmailCount: (state, action) => {
            state.missingCustomerEmailCount = action.payload;
        },
        setSavedSearch: (state, action) => {
            state.requestFiltersState.savedSearch = action.payload;
        },
        setSavedSearchState: (state, action) => {
            state.requestFiltersState = {
                ...defaultRequestFiltersState,
                ...action.payload,
            };
        },
        setCustomerCountAPICompleted: (state, action) => {
            state.isCustomerCountAPICompleted = action.payload;
        },
        incrementCustomerCountAPICompleted: state => {
            state.isCustomerCountAPICompleted += 1;
        },
        decrementCustomerCountAPICompleted: state => {
            state.isCustomerCountAPICompleted -= 1;
        },
        setIsLoadingCustomerCount: (state, action) => {
            state.isLoadingCustomerCount = action.payload;
        },

        setIsExportCampaignDialogOpen: (state, action) => {
            state.isExportCampaignDialogOpen = action.payload;
        },
        setIsLoadingCampaignExport: (state, action) => {
            state.isExportCampaignLoading = action.payload;
        },
        setSelectedCustomersCount: (state, action) => {
            state.selectedCustomerCount = action.payload ?? 0;
        },
        setSelectedCustomersCountLoading: (state, action) => {
            state.isSelectedCustomersCountLoading = action.payload;
        },
        setAddedCustomers: (state, action) => {
            state.addedCustomers = action.payload;
        },
        setRemovedCustomers: (state, action) => {
            state.removedCustomers = action.payload;
        },
        /** Merge one entry into removed (add or replace by id). Use to avoid stale closure in component. */
        mergeIntoRemovedCustomers: (state, action) => {
            const entry = action.payload;
            state.removedCustomers = [
                ...state.removedCustomers.filter(c => c.id !== entry.id),
                entry,
            ];
        },
        /** Remove one id from added. */
        removeIdFromAddedCustomers: (state, action) => {
            const id = action.payload;
            state.addedCustomers = state.addedCustomers.filter(c => c.id !== id);
        },
        /** Merge one entry into added (add or replace by id). */
        mergeIntoAddedCustomers: (state, action) => {
            const entry = action.payload;
            state.addedCustomers = [...state.addedCustomers.filter(c => c.id !== entry.id), entry];
        },
        /** Remove one id from removed. */
        removeIdFromRemovedCustomers: (state, action) => {
            const id = action.payload;
            state.removedCustomers = state.removedCustomers.filter(c => c.id !== id);
        },
        clearCampaignSearchSelection: state => {
            state.addedCustomers = [];
            state.removedCustomers = [];
        },
    },
});

export const {
    setFormState,
    clearFormState,
    setRequestFiltersState,
    clearRequestFiltersState,
    setLoading,
    setCustomerSearchLoading,
    setCustomersWithoutEmailLoading,
    setCampaignCustomersLoading,
    setCampaign,
    setPage,
    setCustomerSearchPage,
    setCampaignCustomersPage,
    setPageToNull,
    setCustomerSearchPageToNull,
    setCampaignCustomersPageToNull,
    setNoDataType,
    setSortColumn,
    setCreateSortColumn,
    setDetailsSortColumn,
    setMissingCustomerEmailCount,
    setSavedSearch,
    setSavedSearchState,
    setCustomerCountAPICompleted,
    incrementCustomerCountAPICompleted,
    decrementCustomerCountAPICompleted,
    setIsLoadingCustomerCount,
    setIsExportCampaignDialogOpen,
    setIsLoadingCampaignExport,
    setSelectedCustomersCount,
    setSelectedCustomersCountLoading,
    setAddedCustomers,
    setRemovedCustomers,
    mergeIntoRemovedCustomers,
    mergeIntoAddedCustomers,
    removeIdFromAddedCustomers,
    removeIdFromRemovedCustomers,
    clearCampaignSearchSelection,
} = requestSlice.actions;

export const selectIsCustomerCountAPICompleted = state => {
    return state.request.isCustomerCountAPICompleted;
};

export const selectIsLoadingCustomerCount = state => {
    return state.request.isLoadingCustomerCount;
};

export const selectFormState = state => {
    return state.request.formState;
};

export const selectRequestFiltersState = state => {
    return state.request.requestFiltersState;
};

export const selectLoading = state => {
    return state.request.isloading;
};

export const selectCustomerSearchLoading = state => {
    return state.request.isCustomerSearchLoading;
};

export const selectCustomersWithoutEmailLoading = state => {
    return state.request.isCustomersWithoutEmailLoading;
};

export const selectCampaignCustomersLoading = state => {
    return state.request.isCustomerSearchLoading;
};

export const selectPage = state => {
    return state.request.page;
};

export const selectCustomerSearchPage = state => {
    return state.request.customerSearchPage;
};

export const selectCampaignCustomersPage = state => {
    return state.request.campaignCustomersPage;
};

export const selectTotalCustomers = state => {
    return state.request.campaignCustomersPage.paginate.totalRecords;
};

export const selectNoData = state => {
    return state.request.noDataType;
};

export const selectSortColumn = state => {
    return state.request.sortColumn;
};

export const selectCreateSortColumn = state => {
    return state.request.createSortColumn;
};

export const selectDetailsSortColumn = state => {
    return state.request.detailsSortColumn;
};

export const selectCampaign = state => {
    return state.request.campaign;
};

export const selectMissingCustomerEmailCount = state => {
    return state.request.missingCustomerEmailCount;
};

export const selectSelectedCustomerCount = state => state.request.selectedCustomerCount;
export const selectSelectedCustomersCountLoading = state =>
    state.request.isSelectedCustomersCountLoading;
export const selectAddedCustomers = state => state.request.addedCustomers;
export const selectRemovedCustomers = state => state.request.removedCustomers;
export const selectIsCampaignCustomersLoading = state => {
    return state.request.isCampaignCustomersLoading;
};

export const selectIsExportCampaignDialogOpen = state => {
    return state.request.isExportCampaignDialogOpen;
};

export const selectIsExportCampaignLoading = state => {
    return state.request.isExportCampaignLoading;
};

export const fetchCampaignAPI = (id, filterString, include) => async (dispatch, getState) => {
    const { request } = getState();
    if (request.isloading) {
        return;
    }

    dispatch(setLoading(true));
    const apiURI = buildApiV3UrlWithQuery(`campaigns/${id}`, filterString, include, 0);

    const response = await axios.get(`${apiURI}`, { withCredentials: true });
    if (response.data === null || response.data === "") {
        dispatch(setNoDataType("noResource"));
    } else {
        dispatch(setNoDataType(""));
    }
    dispatch(setCampaign(response.data));
    dispatch(setLoading(false));
};

export const fetchCampaignsAPI =
    (filterString, include, top, skip, onLanding, paginateDetails) =>
    async (dispatch, getState) => {
        const { isloading, sortColumn } = getState().request;
        const { rowsPerPage } = getState().session;
        top = !top ? rowsPerPage : top;
        if (isloading) {
            return;
        }
        dispatch(setLoading(true));
        const sort = `${sortColumn[0]} ${sortColumn[1] ? "ASC" : "DESC"}`;

        const apiURI = buildApiV3UrlWithQuery(`campaigns`, filterString, include, top, skip, sort);
        const response = await axios.get(`${apiURI}`, { withCredentials: true });

        if (response.data && response.data.count === 0) {
            onLanding ? dispatch(setNoDataType("onLanding")) : dispatch(setNoDataType("onSearch"));
            dispatch(setPageToNull());
        } else {
            dispatch(setNoDataType(""));
            const paginateData = paginateDetails
                ? {
                      pageNumber: paginateDetails.currentPage,
                      startIndex: paginateDetails.startIndex,
                      endIndex: paginateDetails.endIndex,
                      rowsPerPage: paginateDetails.rowsPerPage,
                      totalRecords: response.data.count,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: 19,
                      rowsPerPage: 20,
                      totalRecords: response.data.count,
                  };
            dispatch(
                setPage({
                    data: response.data.value,
                    paginate: paginateData,
                })
            );
        }
        dispatch(setLoading(false));
    };

export const fetchCampaignCustomersAPI =
    (campaignId, filterString, include, top, skip, onLanding, paginateDetails) =>
    async (dispatch, getState) => {
        const { isCampaignCustomersLoading, detailsSortColumn: sortColumn } = getState().request;
        if (isCampaignCustomersLoading) {
            return;
        }
        dispatch(setCampaignCustomersLoading(true));
        const sort = `${sortColumn[0]} ${sortColumn[1] ? "ASC" : "DESC"}`;

        const apiURI = buildApiV3UrlWithQuery(
            `campaigns/${campaignId}/campaign-customers`,
            filterString,
            include,
            top,
            skip,
            sort
        );
        const response = await axios.get(`${apiURI}`, { withCredentials: true });
        if (response.data && response.data.count === 0) {
            dispatch(setCampaignCustomersPageToNull());
        } else {
            dispatch(setNoDataType(""));
            const paginateData = paginateDetails
                ? {
                      pageNumber: paginateDetails.currentPage,
                      startIndex: paginateDetails.startIndex,
                      endIndex: paginateDetails.endIndex,
                      rowsPerPage: paginateDetails.rowsPerPage,
                      totalRecords: response.data.count,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: 19,
                      rowsPerPage: 20,
                      totalRecords: response.data.count,
                  };
            dispatch(
                setCampaignCustomersPage({
                    data: response.data.value,
                    paginate: paginateData,
                })
            );
        }
        dispatch(setCampaignCustomersLoading(false));
    };

export const fetchCampaignDetailsAPI =
    (campaignId, pageSize, pageNumber = 1, onLanding, paginateDetails) =>
    async (dispatch, getState) => {
        const {
            isCampaignCustomersLoading,
            detailsSortColumn: sortColumn,
            formState,
        } = getState().request;
        const { rowsPerPage } = getState().session;
        pageSize = !pageSize ? rowsPerPage : pageSize;

        if (isCampaignCustomersLoading) {
            return;
        }

        const requestBody = {
            pageSize,
            pageNumber,
            orderBy: sortColumn[0],
            orderByDirection: sortColumn[1] ? "DESC" : "ASC",
            nameOrCode: formState.nameOrCode,
        };

        dispatch(setCampaignCustomersLoading(true));
        axios
            .post(buildApiV3Url(`campaigns/${campaignId}/campaign-details`), requestBody, {
                withCredentials: true,
            })
            .then(async response => {
                if (!response.data) {
                    onLanding
                        ? dispatch(setNoDataType("onLanding"))
                        : dispatch(setNoDataType("onCustomerSearch"));
                    dispatch(setCampaignCustomersPageToNull());
                    dispatch(setCampaignCustomersLoading(false));
                } else {
                    dispatch(setNoDataType(""));
                    const paginateData = paginateDetails
                        ? {
                              pageNumber,
                              startIndex: paginateDetails.startIndex,
                              endIndex: paginateDetails.endIndex,
                              rowsPerPage: paginateDetails.rowsPerPage,
                              totalRecords: response.data.count,
                              indeterminate: false,
                          }
                        : {
                              pageNumber,
                              startIndex: 0,
                              endIndex: 19,
                              rowsPerPage: pageSize,
                              totalRecords: response.data.count,
                          };
                    dispatch(
                        setCampaignCustomersPage({
                            data: response.data.value,
                            paginate: paginateData,
                        })
                    );
                    dispatch(setCampaignCustomersLoading(false));
                }
            })
            .catch(() => {
                dispatch(setCampaignCustomersLoading(false));
            });
    };

/**
 * @param {function(): { request: object }} getState
 * @param {number} pageSize
 * @param {number} pageNumber
 * @param {boolean|null} [returnEmptyEmail=null] - override for returnEmptyEmailRecords
 */
export function buildSearchParams(getState, pageSize, pageNumber, returnEmptyEmail = null) {
    const { requestFiltersState, createSortColumn, formState } = getState().request;
    const { nameOrCode, nameOrCodeWhileLoading } = formState;
    const searchVal = nameOrCodeWhileLoading !== "" ? nameOrCodeWhileLoading : nameOrCode;
    return {
        pageSize,
        pageNumber,
        orderBy: createSortColumn[0],
        orderByDirection: createSortColumn[1] ? "ASC" : "DESC",
        includes: "",
        nameOrCode: searchVal,
        purchaseOrderNumber: requestFiltersState.invoiceNumber,
        missing: requestFiltersState.isMissingcertificates,
        missingExposureZones: requestFiltersState.missingExposureZones,
        isBillTo: getCustomerRelationShip(
            requestFiltersState.customerRelationShip,
            customerRelationshipOptions.value.isBillTo
        ),
        isShipTo: getCustomerRelationShip(
            requestFiltersState.customerRelationShip,
            customerRelationshipOptions.value.isShipTo
        ),
        isVendor: requestFiltersState.isVendor,
        certificateImageExists: requestFiltersState.isCertificateImage,
        returnEmptyEmailRecords:
            returnEmptyEmail == null ? requestFiltersState.isMissingEmailAddress : returnEmptyEmail,
        customerCreatedFrom: requestFiltersState.customerCreatedDateAfter
            ? requestFiltersState.customerCreatedDateAfter
            : null,
        customerCreatedTo: requestFiltersState.customerCreatedDateBefore
            ? requestFiltersState.customerCreatedDateBefore
            : null,
        customerModifiedFrom: requestFiltersState.customerModifiedDateAfter
            ? requestFiltersState.customerModifiedDateAfter
            : null,
        customerModifiedTo: requestFiltersState.customerModifiedDateBefore
            ? requestFiltersState.customerModifiedDateBefore
            : null,
        certificateCreatedFrom: requestFiltersState.certificateCreatedDateAfter
            ? requestFiltersState.certificateCreatedDateAfter
            : null,
        certificateCreatedTo: requestFiltersState.certificateCreatedDateBefore
            ? requestFiltersState.certificateCreatedDateBefore
            : null,
        certificateModifiedFrom: requestFiltersState.certificateModifiedDateAfter
            ? requestFiltersState.certificateModifiedDateAfter
            : null,
        certificateModifiedTo: requestFiltersState.certificateModifiedDateBefore
            ? requestFiltersState.certificateModifiedDateBefore
            : null,
        certificateEffectiveFrom: requestFiltersState.certificateEffectiveDateAfter
            ? requestFiltersState.certificateEffectiveDateAfter
            : null,
        certificateEffectiveTo: requestFiltersState.certificateEffectiveDateBefore
            ? requestFiltersState.certificateEffectiveDateBefore
            : null,
        certificateExpirationFrom: requestFiltersState.certificateExpirationDateAfter
            ? requestFiltersState.certificateExpirationDateAfter
            : null,
        certificateExpirationTo: requestFiltersState.certificateExpirationDateBefore
            ? requestFiltersState.certificateExpirationDateBefore
            : null,
        certificateValidatedFrom: requestFiltersState.certificateValidatedDateAfter
            ? requestFiltersState.certificateValidatedDateAfter
            : null,
        certificateValidatedTo: requestFiltersState.certificateValidatedDateBefore
            ? requestFiltersState.certificateValidatedDateBefore
            : null,
        certificateIds: processIdentifiers(
            typeof requestFiltersState.certificateIds === "string"
                ? requestFiltersState.certificateIds
                : undefined,
            value => Number(fixSearchStringForPostgres(value))
        ).filter(id => id !== 0 && Number.isFinite(id)),
        customerCodes: processIdentifiers(
            typeof requestFiltersState.customerCodes === "string"
                ? requestFiltersState.customerCodes
                : undefined,
            fixSearchStringForPostgres
        ),
        status: requestFiltersState.certificateStatus,
        exposureZones: requestFiltersState.certificateRegions,
        customerRegions: requestFiltersState.customerRegions,
        exemptionReasons: requestFiltersState.exemptReasons,
        invalidReasons: requestFiltersState.invalidReasons?.map(Number) || [],
        alternateId: requestFiltersState.alternateId,
        certificatesLabels: requestFiltersState.certificatesLabels,
        excludeCertificateLabels: requestFiltersState.excludeCertificateLabels,
        customersLabels: requestFiltersState.customersLabels,
        isDuplicate: requestFiltersState.isDuplicateRelation,
        excludeCustomerLabels: requestFiltersState.excludeCustomerLabels,
        city: requestFiltersState.city,
        zip: requestFiltersState.zip,
        feinNumber: requestFiltersState.federalTaxID,
        emailAddress: requestFiltersState.email,
        address: requestFiltersState.address,
        phoneNumber: requestFiltersState.phone,
        customerExposures: requestFiltersState.customerExposures,
        customerCustomFields: requestFiltersState.customerCustomFields,
        certificateCustomFields: requestFiltersState.certificateCustomFields,
        activeCertificate: requestFiltersState.isCertActive,
        // Multi-tax params: deduped certificateJurisdictionCodes (SUT local jurisdictions +
        // non-SUT multi-tax codes) and the flat taxSubTaxMappingIds list. Mirrors the live
        // customer search request (fetchCustomersAPI) so the campaign "Request certificates"
        // flow honors the same taxtype/subtaxtype/jurisdiction filters as the customer grid.
        ...buildMultiTaxRequestParams(requestFiltersState, getState().certificate?.taxTypeDetails),
        overrideExemptionReasons: requestFiltersState.overrideExemptReasons,
        overrideExposureZones: requestFiltersState.overrideRegions,
        overrideCreatedFrom: requestFiltersState.overrideCreatedDateAfter || null,
        overrideCreatedTo: requestFiltersState.overrideCreatedDateBefore || null,
        overrideEffectiveFrom: requestFiltersState.overrideEffectiveDateAfter || null,
        overrideEffectiveTo: requestFiltersState.overrideEffectiveDateBefore || null,
        overrideExpirationFrom: requestFiltersState.overrideExpirationDateAfter || null,
        overrideExpirationTo: requestFiltersState.overrideExpirationDateBefore || null,
        overrideLicenseNumber: requestFiltersState.overrideLicenseNumber,
        isOverrideActive:
            requestFiltersState.isOverrideActive === "1"
                ? true
                : requestFiltersState.isOverrideActive === "2"
                  ? false
                  : null,
        denotesExposure: false,
    };
}

export const requestSearch =
    (pageSize, pageNumber = 0, paginateDetails) =>
    async (dispatch, getState) => {
        const state = getState().request;
        const { isCustomerSearchLoading, formState } = state;
        const { nameOrCode, nameOrCodeWhileLoading } = formState;

        if (isCustomerSearchLoading) {
            dispatch(
                setFormState({
                    ...formState,
                    nameOrCodeWhileLoading: nameOrCode,
                })
            );
            return;
        }

        if (!paginateDetails) {
            dispatch(clearCampaignSearchSelection());
        }

        const effectivePageSize = pageSize || getState().session?.rowsPerPage || 20;

        dispatch(setCustomerSearchLoading(true));
        const baseParams = buildSearchParams(getState, effectivePageSize, pageNumber);
        const searchPayload = { ...baseParams, isCampaignSearch: true };
        axios
            .post(buildApiV3Url(`customers/search`), searchPayload, { withCredentials: true })
            .then(async response => {
                if (!response.data) {
                    dispatch(setCustomerSearchPageToNull());
                    dispatch(setNoDataType("onCustomerSearch"));
                } else {
                    dispatch(setNoDataType(""));
                    const { page, isCustomerCountAPICompleted } = getState().request;
                    const pageData = { ...page.paginate };
                    const paginateData = paginateDetails
                        ? {
                              pageNumber: paginateDetails.currentPage,
                              startIndex: paginateDetails.startIndex,
                              endIndex: paginateDetails.endIndex,
                              rowsPerPage: paginateDetails.rowsPerPage,
                              totalRecords: pageData.totalRecords,
                              indeterminate: false,
                          }
                        : {
                              pageNumber,
                              startIndex: 0,
                              endIndex: 19,
                              rowsPerPage: effectivePageSize,
                              totalRecords:
                                  isCustomerCountAPICompleted === 0 ? pageData.totalRecords : 20,
                              indeterminate: !(isCustomerCountAPICompleted === 0),
                          };

                    dispatch(
                        setCustomerSearchPage({
                            data: response.data.value,
                            paginate: paginateData,
                        })
                    );
                }
                if (nameOrCodeWhileLoading !== "") {
                    await dispatch(
                        setFormState({
                            ...formState,
                            nameOrCodeWhileLoading: "",
                        })
                    );
                }
                dispatch(setCustomerSearchLoading(false));
                if (nameOrCodeWhileLoading !== "") {
                    await dispatch(
                        setFormState({
                            ...formState,
                            nameOrCodeWhileLoading: "",
                        })
                    );
                }
            });
        if (!paginateDetails) {
            dispatch(setIsLoadingCustomerCount(true));
            dispatch(incrementCustomerCountAPICompleted());
            axios
                .post(
                    buildApiV3Url(`customers/search-count`),
                    buildSearchParams(getState, effectivePageSize, pageNumber),
                    { withCredentials: true }
                )
                .then(res => {
                    dispatch(decrementCustomerCountAPICompleted());
                    const { page } = getState().request;
                    const pageData = { ...page.paginate };
                    pageData.totalRecords = res?.data;
                    pageData.indeterminate = false;
                    dispatch(
                        setPage({
                            data: page.data,
                            paginate: pageData,
                        })
                    );
                    dispatch(setIsLoadingCustomerCount(false));
                });
        }
    };

/**
 * Fetches count of customers missing email (same filters as search). Called after Next on campaign flow.
 * POST customers/search-count with same filters + returnEmptyEmailRecords: true, denotesExposure: true.
 */
export const getCustomersWithoutEmail = () => async (dispatch, getState) => {
    dispatch(setCustomersWithoutEmailLoading(true));
    const baseParams = buildSearchParams(getState, 20, 1, true);
    const payload = { ...baseParams, denotesExposure: true };
    const response = await axios.post(buildApiV3Url(`customers/search-count`), payload, {
        withCredentials: true,
    });
    if (response && response.data) {
        dispatch(setMissingCustomerEmailCount(response.data));
    }

    dispatch(setCustomersWithoutEmailLoading(false));
};

/**
 * Fetches count of customers (same filters as search). Called after Next on campaign flow.
 * POST customers/search-count with same filters, denotesExposure: true.
 * Result is shown in "Requesting from x customers".
 */
export const getSelectedCustomersCount = () => async (dispatch, getState) => {
    dispatch(setSelectedCustomersCountLoading(true));
    try {
        const payload = { ...buildSearchParams(getState, 20, 1, false), denotesExposure: true };
        const response = await axios.post(buildApiV3Url(`customers/search-count`), payload, {
            withCredentials: true,
        });
        if (response && response.data !== undefined) {
            dispatch(setSelectedCustomersCount(response.data));
        }
    } finally {
        dispatch(setSelectedCustomersCountLoading(false));
    }
};

export const postCampaignExportAPI =
    (pageSize = 20, pageNumber = 0, title) =>
    async (dispatch, getState) => {
        const { isExportCampaignLoading, formState, sortColumn, campaign } = getState().request;
        const { userId, accountId, role } = getState().session;
        const { nameOrCode, nameOrCodeWhileLoading } = formState;
        if (isExportCampaignLoading) {
            return;
        }

        dispatch(setIsLoadingCampaignExport(true));
        const searchVal = nameOrCodeWhileLoading !== "" ? nameOrCodeWhileLoading : nameOrCode;
        const response = await axios.post(
            buildApiV3Url(`export-campaign?title=${title}`),
            {
                pageSize,
                pageNumber,

                orderBy: sortColumn[0],
                orderByDirection: sortColumn[1] ? "DESC" : "ASC",
                nameOrCode: searchVal,
                campaignId: campaign.id,
                avalaraUserId: userId,
                avalaraUserRole: role,
                avaTaxAccountId: accountId,
            },
            { withCredentials: true }
        );
        dispatch(setIsExportCampaignDialogOpen(false));
        dispatch(setIsLoadingCampaignExport(false));
        if (response.status === 200) {
            const baseURL = `https://${getConfig().hostnames.docLockerClient}`;
            window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
            showToast(
                "success",
                "We are working on your export. When it's ready, find it in",
                "export-success",
                true,
                "your downloads",
                () => {
                    hideToast("export-success");
                    window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
                },
                "The time it takes to export data depends on the size of the file and network traffic."
            );
            return;
        }
        showToast("error", `Something went wrong`);
    };

export const updateCampaignCustomersSendStateBulkAPI =
    (campaignId, customerSendStates) => async (dispatch, getState) => {
        const apiURI = buildApiV3Url(`campaigns/${campaignId}/customers/send-status/bulk`);
        const requestBody = {
            customerSendStates: customerSendStates.map(({ customerId, send }) => ({
                customerId,
                send,
            })),
        };
        await axios.put(apiURI, requestBody, { withCredentials: true });

        // Update Redux state with all changes
        const { campaignCustomersPage } = getState().request;
        const sendStateMap = new Map(
            customerSendStates.map(({ customerId, send }) => [customerId, send])
        );
        const updatedData = campaignCustomersPage.data.map(customer =>
            sendStateMap.has(customer.id)
                ? { ...customer, send: sendStateMap.get(customer.id) }
                : customer
        );
        dispatch(
            setCampaignCustomersPage({
                data: updatedData,
                paginate: campaignCustomersPage.paginate,
            })
        );
    };

export const updateCampaignRoundAPI =
    (campaignId, campaignRoundId, updateData) => async (dispatch, getState) => {
        const apiURI = buildApiV3Url(`campaigns/${campaignId}/campaign-rounds/${campaignRoundId}`);
        const response = await axios.put(apiURI, updateData, { withCredentials: true });
        // Update campaign in Redux state
        const { campaign } = getState().request;
        if (campaign?.campaignRounds) {
            const updatedRounds = campaign.campaignRounds.map(round =>
                round.id === campaignRoundId ? response?.data : round
            );
            dispatch(setCampaign({ ...campaign, campaignRounds: updatedRounds }));
        }
        return response?.data;
    };

export const deleteCampaignAPI = campaignId => async () => {
    const apiURI = buildApiV3Url(`campaigns/${campaignId}`);
    await axios.delete(apiURI, { withCredentials: true });

    showToast("success", "Campaign deleted successfully", "delete-campaign-success");
};

export const deleteCampaignRoundAPI =
    ({ campaignId, campaignRoundId }) =>
    async () => {
        const apiURI = buildApiV3Url(`campaigns/${campaignId}/campaign-rounds/${campaignRoundId}`);
        await axios.delete(apiURI, { withCredentials: true });
        showToast(
            "success",
            "Campaign Round deleted successfully",
            "delete-campaign-round-success"
        );
    };

export const addCampaignRoundAPI =
    ({ campaignId, roundDetails }) =>
    async () => {
        const apiURI = buildApiV3Url(`campaigns/${campaignId}/campaign-rounds/`);
        const response = await axios.post(apiURI, roundDetails, { withCredentials: true });
        if (response.status !== 200) {
            showToast("error", `Something went wrong`);
        }
        /** Caller selects this round in the UI. */
        return response.data ?? null;
    };

export default requestSlice.reducer;
